import { SupaCustomerSurveyCompletionsType } from "../../../../4time/src/shared/types/supabaseFragments";
import BaseClient from "../client/base-client";
import { ORDERS_PARAMS } from "../client/types";
import {
  Address,
  AddressInput,
  AddressSuggestion,
  AppSettings,
  Banner,
  CategoriesMapObj,
  Category,
  Coupon,
  CreateOrderData,
  CreatePaymentPayload,
  DeliveryInterval,
  ExpressPointsData,
  ISODate,
  LoginVariables,
  OrderReviewType,
  PaymentFormData,
  PaymentMethod,
  PickupPoint,
  Product,
  ProductReview,
  ProductsQueryParams,
  RegisterResponse,
  RegisterUserCredentials,
  RemoveUserResponse,
  ResetPasswordVariables,
  SearchProductsQueryParams,
  ShippingMethod,
  ShippingZone,
  SmsAuthVariables,
  TOrder,
  TTaxiShippingQuote,
  User,
  UserWithPassword,
  VerificationCodeResponse,
  VerifySMSCodeVariables,
  ProductReviewPayload,
  StoryType,
  CartState,
  Card,
  DeletedCard,
  CreateYooKassaPaymentInput,
  PayWithSavedMethodInput,
  HomeDataSection,
  OrderHistory,
  Promo,
  ProductShelvesSection,
  Parhato2ProductSubscription,
  ProductResults,
  Recipe,
  DeleteInvitationVariables,
  Invitation,
  InviteVariables,
  Notification,
  TinkoffPayment,
  TinkoffGetQr,
  OrderTracking,
  OrderTrackingPayload,
  CheckForExpressPayload,
  ReferralStats,
  NotificationTypesPayload,
  NotificationTypeItem,
  NotificationsPayload,
  UnreadCountResponse,
  MarkAsReadResponse,
  MarkAsReadParams,
  NotificationReadRecord,
  FetchReadRecordsParams,
  FetchReadNotificationIdsParams,
  SavePaymentCardArgs,
  SavePaymentCardResponse,
  SbpPaymentArgs,
  Payments,
  OrderChat,
  OrderChatMessage,
  OrderChatMessagesPage,
  GetOrderChatMessagesPayload,
  SendOrderChatMessagePayload,
  CreateOrderChatAttachmentIntentPayload,
  CreateOrderChatAttachmentIntentResponse,
  ConfirmOrderChatAttachmentPayload,
  MarkOrderChatReadPayload,
  MarkOrderChatReadResult,
  OrderChatUnreadSummary,
  SharedCart,
  CreateSharedCartPayload,
  CustomerWishlist,
  ProductWishlistResponse,
} from "./entities";

export type DataClients = {
  authClient: BaseAuthClient;
  usersClient: BaseUsersClient;
  bannersClient: BaseBannersClient;
  categoriesClient: BaseCategoriesClient;
  productsClient: BaseProductsClient;
  settingsClient: BaseSettingsClient;
  ordersClient: BaseOrdersClient;
  shippingZonesClient: BaseShippingZonesClient;
  paymentMethodsClient: BasePaymentMethodsClient;
  shippingMethodsClient: BaseShippingMethodsClient;
  addressClient: BaseAddressClient;
  couponsClient?: BaseCouponsClient;
  deliveryDatesClient?: BaseDeliveryDatesClient;
  referralClient: BaseReferralClient;
  customerSurveyClient: BaseCustomerSurveyClient;
  storiesClient: BaseStoriesClient;
  cartClient: BaseCartClient;
  cardsClient: BaseCardsClient;
  paymentClient: BasePaymentClient;
  promoClient: BasePromoClient;
  recipeClient: BaseRecipesClient;
  pageItemsClient: BasePageItemsClient;
  notificationsClient: BaseNotificationsClient;
  chatClient: BaseChatClient;
};

export class BasePromoClient extends BaseClient {
  async getPromos(): Promise<Promo[] | null> {
    throw new Error("getPromos is not implemented");
  }
  async getPromoById(id: string): Promise<Promo | null> {
    throw new Error("getPromoById is not implemented");
  }
}

export class BasePageItemsClient extends BaseClient {
  async getPageItemById(id: string): Promise<ProductShelvesSection | null> {
    throw new Error("getPageItemById is not implemented");
  }
  async getHome(): Promise<HomeDataSection[] | null> {
    throw new Error("getHome is not implemented");
  }
}

export class BasePaymentClient extends BaseClient {
  async getTinkoffPayment(orderId: TOrder["id"]): Promise<TinkoffPayment | null> {
    throw new Error("getTinkoffPayment is not implemented");
  }
  async createPaymentTinkoffQr(orderId: TOrder["id"]): Promise<TinkoffGetQr | null> {
    throw new Error("createPaymentTinkoffQr is not implemented");
  }
  async getYooKassaPayment(paymentId: string): Promise<any> {
    throw new Error("getYooKassaPayment is not implemented");
  }
  async createYooKassaPayment(input: CreateYooKassaPaymentInput): Promise<any> {
    throw new Error("createYooKassaPayment is not implemented");
  }
  async payWithSavedMethod(payWithSavedMethodInput: PayWithSavedMethodInput): Promise<any> {
    throw new Error("payWithSavedMethod is not implemented");
  }
  async savePaymentCard(args: SavePaymentCardArgs): Promise<SavePaymentCardResponse> {
    throw new Error("savePaymentCard is not implemented");
  }
  async createSBPPayment(args: SbpPaymentArgs): Promise<Payments | null> {
    throw new Error("createSBPPayment is not implemented");
  }
}

export class BaseChatClient extends BaseClient {
  async getOrCreateOrderChat(orderId: number, options?: { create?: boolean }): Promise<OrderChat | null> {
    throw new Error("getOrCreateOrderChat is not implemented");
  }
  async getOrderChatMessages(params: GetOrderChatMessagesPayload): Promise<OrderChatMessagesPage> {
    throw new Error("getOrderChatMessages is not implemented");
  }
  async sendOrderChatMessage(payload: SendOrderChatMessagePayload): Promise<OrderChatMessage> {
    throw new Error("sendOrderChatMessage is not implemented");
  }
  async createAttachmentIntent(
    payload: CreateOrderChatAttachmentIntentPayload,
  ): Promise<CreateOrderChatAttachmentIntentResponse> {
    throw new Error("createAttachmentIntent is not implemented");
  }
  async uploadAttachmentFile(intent: CreateOrderChatAttachmentIntentResponse, file: File): Promise<void> {
    throw new Error("uploadAttachmentFile is not implemented");
  }
  async confirmAttachment(payload: ConfirmOrderChatAttachmentPayload): Promise<OrderChatMessage> {
    throw new Error("confirmAttachment is not implemented");
  }
  async getRealtimeSessionToken(): Promise<{ token: string; expiresAt: number }> {
    throw new Error("getRealtimeSessionToken is not implemented");
  }
  async markOrderChatRead(payload: MarkOrderChatReadPayload): Promise<MarkOrderChatReadResult> {
    throw new Error("markOrderChatRead is not implemented");
  }
  async getUnreadSummary(): Promise<OrderChatUnreadSummary> {
    throw new Error("getUnreadSummary is not implemented");
  }
}

export class BaseBannersClient extends BaseClient {
  async getBanners(): Promise<Banner[]> {
    throw new Error("getBanners is not implemented");
  }
}

export class BaseDeliveryDatesClient extends BaseClient {
  async getDeliveryDates(shippingMethodId?: string): Promise<ISODate[]> {
    throw new Error("getDeliveryDates is not implemented");
  }
  async getDeliveryIntervals(shippingMethodId?: string, date?: ISODate): Promise<DeliveryInterval[]> {
    throw new Error("getDeliveryIntervals is not implemented");
  }
}

export class BaseCategoriesClient extends BaseClient {
  async getAllCategories(): Promise<CategoriesMapObj> {
    throw new Error("getAllCategories is not implemented");
  }
  async getMainCategories(): Promise<Category[]> {
    throw new Error("getMainCategories is not implemented");
  }
  async getSubCategories(id: string): Promise<Category[]> {
    throw new Error("getSubCategories is not implemented");
  }
  async getCategoryById(id: string): Promise<Category | null> {
    throw new Error("getCategoryById is not implemented");
  }
}

export class BaseOrdersClient extends BaseClient {
  async getUnpaidOrdersId(): Promise<TOrder["id"][]> {
    throw new Error("getUnpaidOrdersId is not implemented");
  }
  async getUnreviewedOrdersId(): Promise<TOrder["id"][]> {
    throw new Error("getUnreviewedOrdersId is not implemented");
  }
  async getOrderById(id: string): Promise<TOrder> {
    throw new Error("getOrderById is not implemented");
  }
  async getOrders(params?: ORDERS_PARAMS): Promise<TOrder[]> {
    throw new Error("getOrders is not implemented");
  }
  async getOrdersHistory(params?: { page: number; per_page: number }): Promise<OrderHistory[]> {
    throw new Error("getOrdersHistory is not implemented");
  }
  async createOrder(orderData: CreateOrderData): Promise<Partial<TOrder>> {
    throw new Error("createOrder is not implemented");
  }
  async getReviewSetOrderById(id: string): Promise<OrderReviewType | null> {
    throw new Error("getReviewSetOrderById is not implemented");
  }
  async createPayment(payload: CreatePaymentPayload): Promise<PaymentFormData> {
    throw new Error("createPayment is not implemented");
  }
  async addReviewOrder(orderReqData: OrderReviewType): Promise<OrderReviewType | null> {
    throw new Error("addReviewOrder is not implemented");
  }
  async getOrderTracking(orderReqData: OrderTrackingPayload): Promise<OrderTracking | null> {
    throw new Error("getOrderTracking is not implemented");
  }
  async orderCancel(orderId: string): Promise<Partial<TOrder>> {
    throw new Error("orderCancel is not implemented");
  }
  async createTaxiOrder(orderId: TOrder["id"]): Promise<any> {
    throw new Error("createTaxiOrder is not implemented");
  }
}

export class BaseNotificationsClient extends BaseClient {
  async getNotificationTypes(params?: NotificationTypesPayload): Promise<NotificationTypeItem[]> {
    throw new Error("getNotificationTypes is not implemented");
  }
  async getNotifications(params?: NotificationsPayload): Promise<Notification[]> {
    throw new Error("getNotifications is not implemented");
  }
  async markAsRead(params: MarkAsReadParams): Promise<MarkAsReadResponse> {
    throw new Error("markAsRead is not implemented");
  }
  async getUnreadCount(): Promise<UnreadCountResponse> {
    throw new Error("getUnreadCount is not implemented");
  }
  async fetchReadRecords(params: FetchReadRecordsParams): Promise<NotificationReadRecord[]> {
    throw new Error("fetchReadRecords is not implemented");
  }
  async fetchReadNotificationIds(params: FetchReadNotificationIdsParams): Promise<number[]> {
    throw new Error("fetchReadNotificationIds is not implemented");
  }
}

export class BaseProductsClient extends BaseClient {
  async searchProducts(params: SearchProductsQueryParams, zoneId: string): Promise<Product[]> {
    throw new Error("searchProducts is not implemented");
  }
  async getProducts(props?: ProductsQueryParams): Promise<ProductResults> {
    throw new Error("getProducts is not implemented");
  }
  async getOnSaleProducts(props?: ProductsQueryParams): Promise<ProductResults> {
    throw new Error("getOnSaleProducts is not implemented");
  }
  async getFeaturedProducts(props?: ProductsQueryParams): Promise<ProductResults> {
    throw new Error("getFeaturedProducts is not implemented");
  }
  async getNewestProducts(props?: ProductsQueryParams): Promise<ProductResults> {
    throw new Error("getNewestProducts is not implemented");
  }
  async getCustomersChoiceProducts(props?: ProductsQueryParams): Promise<ProductResults> {
    throw new Error("getCustomersChoiceProducts is not implemented");
  }
  async getProductById(id: string, zoneId: string, params?: ProductsQueryParams): Promise<Product> {
    throw new Error("getProductById is not implemented");
  }
  async getIncludeProducts(include: string[], params: { per_page?: string; zoneId: string }): Promise<Product[]> {
    throw new Error("getIncludeProducts is not implemented");
  }
  async getProductReviews(id: Product["id"]): Promise<ProductReview[]> {
    throw new Error("getProductReviews is not implemented");
  }
  async addProductReview(productReviewPayload: ProductReviewPayload): Promise<ProductReview> {
    throw new Error("addProductReview is not implemented");
  }
  async getAllProductsSubscriptions(): Promise<Parhato2ProductSubscription[]> {
    throw new Error("getAllProductsSubscriptions is not implemented");
  }
  async addProductSubscription(productId: Product["id"]): Promise<Parhato2ProductSubscription | null> {
    throw new Error("addProductSubscription is not implemented");
  }

  async getWishlist(): Promise<CustomerWishlist | null> {
    throw new Error("getWishlist is not implemented");
  }

  async updateProductsWishlist(productIds: Product["id"][]): Promise<ProductWishlistResponse> {
    throw new Error("updateProductsWishlist is not implemented");
  }
}

export class BaseSettingsClient extends BaseClient {
  async getSettings(): Promise<Partial<AppSettings> | null> {
    throw new Error("getSettings is not implemented");
  }
}

export class BaseAuthClient extends BaseClient {
  async login(data: LoginVariables): Promise<User> {
    throw new Error("login is not implemented");
  }
  async sendVerificationCode(phone: string): Promise<any> {
    throw new Error("sendVerificationCode is not implemented");
  }
  async verifyCode(params: VerifySMSCodeVariables): Promise<any> {
    throw new Error("verifyCode is not implemented");
  }
  async resetPassword(params: ResetPasswordVariables): Promise<any> {
    throw new Error("resetPassword is not implemented");
  }
  async register(params: RegisterUserCredentials): Promise<RegisterResponse> {
    throw new Error("register is not implemented");
  }
  async setToken(token: string, supabaseAuth?: User["supabaseAuth"]): Promise<void> {
    throw new Error("setToken is not implemented");
  }
  async getSmsCode(phone: string): Promise<VerificationCodeResponse> {
    throw new Error("getSmsCode is not implemented");
  }
  async getUcallerCode(phone: string): Promise<VerificationCodeResponse> {
    throw new Error("getUcallerCode is not implemented");
  }
  async authBySmsCode(params: SmsAuthVariables): Promise<any> {
    throw new Error("authBySmsCode is not implemented");
  }
  async getRegisterVerificationCode(phone: string): Promise<VerificationCodeResponse> {
    throw new Error("getRegisterVerificationCode is not implemented");
  }
  async getTGVerificationToken(): Promise<{ token: string }> {
    throw new Error("getTGVerificationToken is not implemented");
  }
  async authByTGToken(token: string): Promise<User | { customer_confirmed: false }> {
    throw new Error("authByTGToken is not implemented");
  }
  async logout(): Promise<void> {}
  async initAuth(): Promise<void> {}
}

export class BaseShippingZonesClient extends BaseClient {
  async getShippingZones(): Promise<ShippingZone[]> {
    throw new Error("getShippingZones is not implemented");
  }
  async getCheckAddressForExpress(address: CheckForExpressPayload): Promise<ExpressPointsData> {
    throw new Error("getCheckAddressForExpress is not implemented");
  }
}

export class BasePaymentMethodsClient extends BaseClient {
  async getPaymentMethods(): Promise<PaymentMethod[]> {
    throw new Error("getPaymentMethods is not implemented");
  }
}

export class BaseShippingMethodsClient extends BaseClient {
  async getShippingMethods(zoneId?: string, productsWeight?: number): Promise<ShippingMethod[]> {
    throw new Error("getShippingMethods is not implemented");
  }
  async getPickupPoints(zoneId?: string): Promise<PickupPoint[]> {
    throw new Error("getPickupPoints is not implemented");
  }
  async calculateTaxiShipping(address: Address): Promise<TTaxiShippingQuote | null> {
    throw new Error("calculateTaxiShipping is not implemented");
  }
}

export class BaseUsersClient extends BaseClient {
  async updateProfile(userId: string, updateData: Partial<UserWithPassword>): Promise<Partial<User>> {
    throw new Error("updateProfile is not implemented");
  }
  async getProfile(userId: string): Promise<User> {
    throw new Error("getProfile is not implemented");
  }
  async deleteMe(): Promise<RemoveUserResponse> {
    throw new Error("deleteMe is not implemented");
  }
}

export class BaseCouponsClient extends BaseClient {
  async getCoupon(code: string): Promise<Coupon | null> {
    throw new Error("getCoupon is not implemented");
  }
}

export class BaseCardsClient extends BaseClient {
  async getCards(): Promise<Card[]> {
    throw new Error("getCards is not implemented");
  }
  async deleteCard(variables: { id: string }): Promise<DeletedCard> {
    throw new Error("deleteCard is not implemented");
  }
}

export class BaseRecipesClient extends BaseClient {
  async getRecipes(): Promise<Recipe[]> {
    throw new Error("getRecipes is not implemented");
  }
  async getRecipeById(recipeId: Recipe["id"]): Promise<Recipe | null> {
    throw new Error("getRecipeById is not implemented");
  }
}

export class BaseAddressClient extends BaseClient {
  async getAddressSuggestion(
    address: string,
    abortSignal?: AbortSignal,
    searchPrefix?: string,
  ): Promise<AddressSuggestion[] | null> {
    throw new Error("getAddressSuggestion is not implemented");
  }
  async getCustomerAddresses(customerId: User["id"]): Promise<Address[] | null> {
    throw new Error("getCustomerAddresses is not implemented");
  }
  async deleteCustomerAddress(addressId: Address["id"]): Promise<Address["id"] | null> {
    throw new Error("deleteCustomerAddress is not implemented");
  }
  async updateCustomerAddress(customerAddress: Address, customerId: User["id"] | undefined): Promise<Address | null> {
    throw new Error("updateCustomerAddress is not implemented");
  }
  async createCustomerAddress(customerAddressInput: AddressInput, customerId: User["id"]): Promise<Address | null> {
    throw new Error("createCustomerAddress is not implemented");
  }
}

export class BaseReferralClient extends BaseClient {
  async invite(variables: InviteVariables): Promise<any> {
    throw new Error("invite is not implemented");
  }
  async getInvitations(): Promise<Invitation[]> {
    throw new Error("getInvitations is not implemented");
  }
  async deleteInvitation(variables: DeleteInvitationVariables): Promise<any> {
    throw new Error("deleteInvitation is not implemented");
  }
  async getReferralPoints(): Promise<number> {
    throw new Error("getReferralPoints is not implemented");
  }
  async checkWasInvited(variables: InviteVariables): Promise<Invitation | null> {
    throw new Error("checkWasInvited is not implemented");
  }
  async getReferralStats(referralId: string): Promise<ReferralStats | null> {
    throw new Error("getReferralStats is not implemented");
  }
}

export class BaseCustomerSurveyClient extends BaseClient {
  async getCustomerSurveyAnswers(userPhone: User["phone"]): Promise<SupaCustomerSurveyCompletionsType | null> {
    throw new Error("getCustomerSurveyAnswers is not implemented");
  }
  async createCustomerSurveyAnswers(
    payload: Omit<SupaCustomerSurveyCompletionsType, "id" | "created_at">,
  ): Promise<void> {
    throw new Error("createCustomerSurveyAnswers is not implemented");
  }
}

export class BaseStoriesClient extends BaseClient {
  async getStories(): Promise<StoryType[]> {
    throw new Error("getStories is not implemented");
  }
}

export class BaseCartClient extends BaseClient {
  async updateCart(cart: CartState, shippingZone: string): Promise<CartState | null> {
    throw new Error("updateCart is not implemented");
  }

  async createSharedCart(cart: CreateSharedCartPayload): Promise<SharedCart | null> {
    throw new Error("createSharedCart is not implemented");
  }
  async getSharedCartById(id: SharedCart["id"]): Promise<SharedCart | null> {
    throw new Error("getSharedCartById is not implemented");
  }

  async getAllSharedCarts(params?: { page?: number; perPage?: number }): Promise<SharedCart[] | null> {
    throw new Error("getAllSharedCarts is not implemented");
  }
}
